import React, { useEffect, useRef } from 'react';
import './DocumentPreview.css';

const DocumentPreview = ({ document, previewData, loading, onClose }) => {
  const modalRef = useRef(null);

  // Support both old prop structure (previewData) and new structure (document with preview)
  const preview = previewData?.preview || document?.preview;
  const isPdf = previewData?.isPdf || document?.isPdf;
  const filename = previewData?.filename || document?.filename;
  const mimeType = previewData?.mimeType || document?.mimeType;
  const message = previewData?.message || document?.message;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent right-click
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const modal = modalRef.current;
    if (modal) {
      modal.addEventListener('contextmenu', handleContextMenu);
      return () => modal.removeEventListener('contextmenu', handleContextMenu);
    }
  }, []);

  return (
    <div className="doc-preview-overlay">
      <div className="doc-preview-modal" ref={modalRef}>
        {/* Header */}
        <div className="doc-preview-header">
          <div className="doc-preview-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span>{document?.typeName || document?.type}</span>
          </div>
          <button className="doc-preview-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="doc-preview-content">
          {loading ? (
            <div className="doc-preview-loading">
              <div className="preview-spinner"></div>
              <p>Loading preview...</p>
            </div>
          ) : isPdf && preview ? (
            /* Full PDF viewer */
            <div className="doc-preview-pdf-viewer">
              <embed
                src={preview}
                type="application/pdf"
                className="pdf-embed"
              />
            </div>
          ) : preview && !isPdf ? (
            <div className="doc-preview-image-container">
              <img
                src={preview}
                alt={document?.typeName}
                className="doc-preview-image"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
          ) : isPdf ? (
            <div className="doc-preview-pdf">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <p className="pdf-filename">{filename}</p>
              <p className="pdf-message">{message || 'PDF preview loading...'}</p>
            </div>
          ) : (
            <div className="doc-preview-unavailable">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
              </svg>
              <p>Preview not available</p>
              <span className="preview-hint">Document can still be prepared for upload</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="doc-preview-footer">
          <div className="preview-info">
            <span className="preview-filename">{filename}</span>
            <span className="preview-type">{mimeType}</span>
          </div>
          <div className="preview-security">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span>Secure preview</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
